import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import logger from '../utils/logger';

export interface DatasetSelection {
  selectedColumns: string[];
  selectedRows: number[];
}

interface SelectionStore {
  // 状態
  selections: Record<string, DatasetSelection>;
  
  // アクション
  getSelection: (datasetId: string) => DatasetSelection;
  updateSelection: (datasetId: string, updates: Partial<DatasetSelection>) => void;
  selectColumns: (datasetId: string, columns: string[]) => void;
  selectRows: (datasetId: string, rows: number[]) => void;
  resetSelection: (datasetId: string) => void;
  removeSelection: (datasetId: string) => void;
}

const defaultSelection: DatasetSelection = {
  selectedColumns: [],
  selectedRows: []
};

export const useSelectionStore = create<SelectionStore>()(
  devtools(
    (set, get) => ({
      // 初期状態
      selections: { /* no-op */ },
      
      // 選択状態取得（存在しない場合はデフォルト値を返す）
      getSelection: (datasetId) => {
        const selections = get().selections;
        return selections[datasetId] || defaultSelection;
      },
      
      // 選択状態更新
      updateSelection: (datasetId, updates) => {
        logger.assert(datasetId != null && datasetId.length > 0, 'Dataset ID must not be empty');
        
        
        set((state) => ({
          selections: {
            ...state.selections,
            [datasetId]: {
              ...state.getSelection(datasetId),
              ...updates
            }
          }
        }));
      },
      
      // カラム選択
      selectColumns: (datasetId, columns) => {
        logger.assert(datasetId != null && datasetId.length > 0, 'Dataset ID must not be empty');
        logger.assert(columns != null, 'Columns must not be null');
        
        const previousColumns = get().getSelection(datasetId).selectedColumns;
        logger.info('[SelectionStore] Selecting columns', {
          datasetId,
          columnCount: columns.length,
          previousCount: previousColumns.length,
          columns: columns.length <= 10 ? columns : columns.slice(0, 10).concat(['...'])
        });
        
        get().updateSelection(datasetId, { selectedColumns: columns });
      },
      
      // 行選択
      selectRows: (datasetId, rows) => {
        logger.assert(datasetId != null && datasetId.length > 0, 'Dataset ID must not be empty');
        logger.assert(rows != null, 'Rows must not be null');
        
        const previousRows = get().getSelection(datasetId).selectedRows;
        const action = rows.length === 0 ? 'Clearing' : 
                       rows.length > previousRows.length ? 'Adding' : 
                       rows.length < previousRows.length ? 'Removing' : 'Updating';
        
        logger.info('[SelectionStore] Row selection changed', {
          datasetId,
          action,
          rowCount: rows.length,
          previousCount: previousRows.length,
          delta: rows.length - previousRows.length,
          sampleRows: rows.length > 0 ? rows.slice(0, 5) : []
        });
        
        get().updateSelection(datasetId, { selectedRows: rows });
      },
      
      // 選択状態リセット
      resetSelection: (datasetId) => {
        logger.assert(datasetId != null && datasetId.length > 0, 'Dataset ID must not be empty');
        
        const previousSelection = get().getSelection(datasetId);
        logger.info('[SelectionStore] Resetting selection', {
          datasetId,
          previousColumnsCount: previousSelection.selectedColumns.length,
          previousRowsCount: previousSelection.selectedRows.length
        });
        
        set((state) => ({
          selections: {
            ...state.selections,
            [datasetId]: { ...defaultSelection }
          }
        }));
      },
      
      // データセットが削除された時のクリーンアップ
      removeSelection: (datasetId) => {
        logger.assert(datasetId != null && datasetId.length > 0, 'Dataset ID must not be empty');
        
        const selectionToRemove = get().selections[datasetId];
        if (selectionToRemove) {
          logger.info('[SelectionStore] Removing selection', {
            datasetId,
            hadSelectedColumns: selectionToRemove.selectedColumns.length,
            hadSelectedRows: selectionToRemove.selectedRows.length
          });
        }

        set((state) => {
          const { [datasetId]: removed, ...remainingSelections } = state.selections;
          void removed; // 未使用変数の警告を抑制
          return { selections: remainingSelections };
        });
      }
    }),
    { name: 'SelectionStore' }
  )
);